import Botpoison from '@botpoison/browser';
import Cookies from 'js-cookie';

declare global {
	interface Window {
		dataLayer: unknown[];
	}
}

function getLanguage(): string {
	return document.documentElement.lang;
}

function setupOutsideClick(handler: () => void): void {
	setTimeout(() => {
		document.body.addEventListener('click', handler, {
			once: true,
		});
	}, 0);
}

function toggleMobileMenu(): void {
	const navElement = document.querySelector('nav');
	const overlayElement = document.querySelector('.overlay');
	const navIconElement = document.querySelector('#nav-icon');
	const menuToggleElement = document.querySelector('#menuToggle');

	if (navElement && overlayElement && navIconElement) {
		// Check if the nav is currently open (i.e., has the 'open' class)
		const isOpen = navElement.classList.contains('open');

		// Toggle classes based on current state
		navElement.classList.toggle('open', !isOpen);
		overlayElement.classList.toggle('active', !isOpen);
		navIconElement.classList.toggle('open', !isOpen);
		menuToggleElement?.setAttribute('aria-expanded', String(!isOpen));

		// Reset any open submenu drill-down panel when the menu is toggled.
		document
			.querySelectorAll('.sub-menu.open')
			.forEach(submenu => submenu.classList.remove('open'));
	}
}

function toggleLanguageMenu(open: boolean): void {
	const language = document.querySelector('.language');
	const languageButton = document.querySelector('.language-button');

	// Allready open, bail out.
	if (language?.classList.contains('active') && open) {
		return;
	}

	language?.classList[open ? 'add' : 'remove']('active');
	languageButton?.setAttribute('aria-expanded', String(open));

	if (open) {
		setupOutsideClick(() => {
			toggleLanguageMenu(false);
		});
	}
}

function vimeoSrcWithControls(src: string): string {
	try {
		const url = new URL(src);
		url.searchParams.delete('background');
		url.searchParams.set('autoplay', '1');
		url.searchParams.set('muted', '0');
		return url.toString();
	} catch {
		return src;
	}
}

// A single lightbox serves two modes: the background-video showreels and the
// interactive configurator demo. The trigger decides which mode is shown.
function setupLightbox(): void {
	const lightbox = document.querySelector('.lightbox');
	const closeButton = lightbox?.querySelector('.lightbox-close');

	if (!lightbox || !closeButton) {
		return;
	}

	const videoFrame = lightbox.querySelector<HTMLIFrameElement>(
		'.lightbox-video-iframe',
	);
	const configFrame = lightbox.querySelector<HTMLIFrameElement>(
		'.configurator-lightbox-iframe',
	);
	const configImage = lightbox.querySelector<HTMLImageElement>(
		'.configurator-lightbox-image',
	);
	const frameWrap = configFrame?.closest('.configurator-lightbox-frame');
	const tabs =
		lightbox.querySelectorAll<HTMLButtonElement>('.configurator-tab');
	const hiddenInput = lightbox.querySelector<HTMLInputElement>(
		'input[name="interested-configurator"]',
	);
	const leadForm = lightbox.querySelector<HTMLFormElement>(
		'.configurator-lead-form',
	);
	const formSuccess = lightbox.querySelector<HTMLElement>(
		'.configurator-form-success',
	);

	const open = (mode: 'video' | 'configurator'): void => {
		lightbox.classList.remove('is-video', 'is-configurator');
		lightbox.classList.add(`is-${mode}`, 'open');
		lightbox.setAttribute('aria-hidden', 'false');
		document.body.classList.add('lightbox-open');
	};

	const close = (): void => {
		lightbox.classList.remove('open', 'is-video', 'is-configurator');
		lightbox.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('lightbox-open');
		if (videoFrame) {
			videoFrame.src = '';
		}
		if (configFrame) {
			configFrame.src = '';
		}
		if (configImage) {
			configImage.src = '';
		}
		// Reset the lead form so a reopened lightbox starts fresh.
		if (leadForm && formSuccess) {
			leadForm.hidden = false;
			formSuccess.hidden = true;
			leadForm.reset();
			leadForm
				.querySelector<HTMLElement>('.configurator-form-error')
				?.setAttribute('hidden', '');
		}
	};

	// Video mode: the fullscreen buttons on the background showreels.
	if (videoFrame) {
		document.querySelectorAll('.video-fullscreen').forEach(button => {
			button.addEventListener('click', () => {
				const container = button.closest('.container');
				const sourceIframe = container?.querySelector('iframe');
				if (!sourceIframe) {
					return;
				}
				videoFrame.src = vimeoSrcWithControls(sourceIframe.src);
				open('video');
			});
		});
	}

	// Configurator mode: the "try it yourself" triggers and demo switching.
	if (configFrame && tabs.length) {
		if (frameWrap) {
			// Hide the loading state once the configurator has rendered.
			configFrame.addEventListener('load', () => {
				frameWrap.classList.remove('loading');
			});
		}

		const selectTab = (tab: HTMLButtonElement): void => {
			tabs.forEach(candidate => {
				const isActive = candidate === tab;
				candidate.classList.toggle('active', isActive);
				candidate.setAttribute(
					'aria-selected',
					isActive ? 'true' : 'false',
				);
			});

			// Pass the viewed configurator along with the lead so sales knows
			// what the visitor was interested in.
			if (hiddenInput) {
				hiddenInput.value = tab.dataset['title'] ?? '';
			}

			// Configurators without a live demo (e.g. modular, temporarily)
			// fall back to a static image instead of the iframe.
			const imageSrc = tab.dataset['image'];
			if (imageSrc && configImage) {
				frameWrap?.classList.remove('loading');
				configFrame.hidden = true;
				configFrame.src = '';
				configImage.src = imageSrc;
				configImage.alt = tab.dataset['title'] ?? '';
				configImage.hidden = false;
				return;
			}

			if (configImage) {
				configImage.hidden = true;
				configImage.src = '';
			}
			configFrame.hidden = false;

			const src = tab.dataset['src'];
			// Only (re)load the iframe when the source actually changes.
			if (src && configFrame.getAttribute('src') !== src) {
				frameWrap?.classList.add('loading');
				configFrame.src = src;
			}
		};

		tabs.forEach(tab => {
			tab.addEventListener('click', () => selectTab(tab));
		});

		document.querySelectorAll('.configurator-trigger').forEach(trigger => {
			trigger.addEventListener('click', event => {
				event.preventDefault();
				// Load the configurator only when opened, keeping the heavy
				// WebGL viewer out of the initial page load.
				const active =
					lightbox.querySelector<HTMLButtonElement>(
						'.configurator-tab.active',
					) ?? tabs[0];
				if (active) {
					selectTab(active);
				}
				open('configurator');
			});
		});
	}

	// Submit the lead form via fetch so we can show inline feedback in the
	// lightbox instead of navigating away to the contact page.
	if (leadForm) {
		const submitButton = leadForm.querySelector<HTMLButtonElement>(
			'button[type="submit"]',
		);
		const errorEl = leadForm.querySelector<HTMLElement>(
			'.configurator-form-error',
		);

		// Botpoison's automatic handling only covers forms that submit
		// natively (it solves the challenge and then submits the form itself),
		// so this form carries the key under a different attribute and solves
		// the challenge here before posting it along with the fetch.
		const botpoisonKey = leadForm.dataset['botpoisonKey'];
		const botpoison = botpoisonKey
			? new Botpoison({ publicKey: botpoisonKey })
			: undefined;

		leadForm.addEventListener('submit', event => {
			event.preventDefault();
			errorEl?.setAttribute('hidden', '');

			if (submitButton) {
				submitButton.disabled = true;
				const sending = submitButton.dataset['sending'];
				if (sending) {
					submitButton.textContent = sending;
				}
			}

			const body = new FormData(leadForm);

			void Promise.resolve(botpoison?.challenge())
				.then(result => {
					if (result) {
						body.append('_botpoison', result.solution);
					}

					return fetch(leadForm.action, {
						method: 'POST',
						body,
						headers: { Accept: 'application/json' },
					});
				})
				.then(response => {
					if (!response.ok) {
						throw new Error(`Request failed: ${response.status}`);
					}
					leadForm.hidden = true;
					formSuccess?.removeAttribute('hidden');
				})
				.catch(() => {
					errorEl?.removeAttribute('hidden');
				})
				.finally(() => {
					if (submitButton) {
						submitButton.disabled = false;
						const label = submitButton.dataset['label'];
						if (label) {
							submitButton.textContent = label;
						}
					}
				});
		});
	}

	closeButton.addEventListener('click', close);

	lightbox.addEventListener('click', event => {
		if (event.target === lightbox) {
			close();
		}
	});

	document.addEventListener('keydown', event => {
		if (event.key === 'Escape' && lightbox.classList.contains('open')) {
			close();
		}
	});
}

function setupMenu(): void {
	document.querySelector('.menu-button')?.addEventListener('click', event => {
		event.preventDefault();

		toggleMobileMenu();
	});
}

function setupLanguageMenu(): void {
	document
		.querySelector('.language-button')
		?.addEventListener('click', event => {
			event.preventDefault();

			toggleLanguageMenu(true);
		});

	document.querySelectorAll('.language-menu a').forEach(element => {
		element.addEventListener('click', event => {
			const target = event.target as HTMLAnchorElement | null;
			if (!target) {
				return;
			}

			const lang = target.dataset['lang'];
			if (!lang) {
				return;
			}

			event.preventDefault();

			Cookies.set('firebase-language-override', lang, {
				expires: 180,
				secure: location.protocol === 'https:',
				sameSite: 'strict',
			});

			window.location.href = target.href;
		});
	});
}

function setupOfferToggle(): void {
	const buttons = document.querySelectorAll<HTMLButtonElement>(
		'[data-offer-target]',
	);
	const panels = document.querySelectorAll<HTMLElement>('[data-offer-panel]');

	if (!buttons.length || !panels.length) {
		return;
	}

	buttons.forEach(button => {
		button.addEventListener('click', () => {
			const target = button.dataset['offerTarget'];
			if (!target) {
				return;
			}

			buttons.forEach(candidate => {
				const isActive = candidate === button;
				candidate.classList.toggle('active', isActive);
				candidate.setAttribute(
					'aria-selected',
					isActive ? 'true' : 'false',
				);
			});

			panels.forEach(panel => {
				panel.hidden = panel.dataset['offerPanel'] !== target;
			});

			const activePanel = Array.from(panels).find(
				panel => panel.dataset['offerPanel'] === target,
			);
			if (activePanel) {
				replayReveal(activePanel);
			}
		});
	});
}

// How long each configurator stays in view before the carousel advances.
const FEATURE_CONFIGURATOR_ROTATE_MS = 8000;

// Homepage "3D viewer & configurator" showcase: lets visitors switch between
// configurator demos in the embedded iframe, same idea as the "try it
// yourself" lightbox but inline on the page instead of in a modal. Also
// auto-rotates through the configurators, visualised by a progress bar,
// pausing whenever the visitor hovers the demo.
function setupFeatureConfiguratorTabs(): void {
	const section = document.querySelector('.feature-section');
	const frame = section?.querySelector<HTMLElement>('.feature-demo');
	const iframe = frame?.querySelector<HTMLIFrameElement>(
		'.feature-configurator-iframe',
	);
	const image = frame?.querySelector<HTMLImageElement>(
		'.feature-configurator-image',
	);
	const tabs = section
		? Array.from(
				section.querySelectorAll<HTMLButtonElement>(
					'.feature-configurator-tab:not([disabled])',
				),
			)
		: [];
	const panels = section?.querySelectorAll<HTMLElement>(
		'.feature-configurator-panel',
	);
	const progressBar = section?.querySelector<HTMLElement>(
		'.feature-configurator-progress-bar',
	);

	if (!section || !frame || !iframe || !image || !tabs.length) {
		return;
	}

	iframe.addEventListener('load', () => {
		frame.classList.remove('loading');
	});

	const selectTab = (tab: HTMLButtonElement | undefined): void => {
		if (!tab) {
			return;
		}

		tabs.forEach(candidate => {
			const isActive = candidate === tab;
			candidate.classList.toggle('active', isActive);
			candidate.setAttribute(
				'aria-selected',
				isActive ? 'true' : 'false',
			);
		});

		const panelId = tab.dataset['panel'];
		panels?.forEach(panel => {
			panel.hidden = panelId !== undefined && panel.id !== panelId;
		});

		// Configurators without a live demo (e.g. modular, temporarily)
		// fall back to a static image instead of the iframe.
		const imageSrc = tab.dataset['image'];
		if (imageSrc) {
			frame.classList.remove('loading');
			iframe.hidden = true;
			iframe.src = '';
			image.src = imageSrc;
			image.alt = tab.dataset['title'] ?? '';
			image.hidden = false;
			return;
		}

		image.hidden = true;
		image.src = '';

		const src = tab.dataset['src'];
		if (src) {
			iframe.hidden = false;
			if (iframe.getAttribute('src') !== src) {
				frame.classList.add('loading');
				iframe.src = src;
			}
		}
	};

	// Don't lead with the same configurator on every page load.
	let currentIndex = Math.floor(Math.random() * tabs.length);
	let isHovering = false;
	let progressAnimation: Animation | null = null;

	const startProgress = (): void => {
		if (!progressBar) {
			return;
		}

		progressAnimation?.cancel();
		progressAnimation = progressBar.animate(
			[{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
			{
				duration: FEATURE_CONFIGURATOR_ROTATE_MS,
				easing: 'linear',
				fill: 'forwards',
			},
		);
		progressAnimation.addEventListener('finish', () => {
			currentIndex = (currentIndex + 1) % tabs.length;
			selectTab(tabs[currentIndex]);
			startProgress();
		});
		if (isHovering) {
			progressAnimation.pause();
		}
	};

	tabs.forEach((tab, index) => {
		tab.addEventListener('click', () => {
			currentIndex = index;
			selectTab(tab);
			startProgress();
		});
	});

	// Hovering the demo pauses the rotation and its progress bar, so the
	// content under the cursor never changes on you.
	frame.addEventListener('mouseenter', () => {
		isHovering = true;
		progressAnimation?.pause();
	});
	frame.addEventListener('mouseleave', () => {
		isHovering = false;
		progressAnimation?.play();
	});

	selectTab(tabs[currentIndex]);
	startProgress();
}

// How long (max) siblings in the same row/grid wait for each other before
// fading in, so cards cascade in one after another instead of all at once.
const REVEAL_STAGGER_STEP_MS = 80;
const REVEAL_STAGGER_MAX_STEPS = 6;

// Gives each reveal target a --reveal-delay based on its position among its
// siblings (e.g. cards in the same grid), so a row cascades in left to
// right instead of every card fading in at the same instant.
function assignRevealStagger(targets: HTMLElement[]): void {
	const seen = new Map<Element, number>();

	targets.forEach(target => {
		const parent = target.parentElement;
		if (!parent) {
			return;
		}

		const index = seen.get(parent) ?? 0;
		seen.set(parent, index + 1);

		const step = Math.min(index, REVEAL_STAGGER_MAX_STEPS);
		target.style.setProperty(
			'--reveal-delay',
			`${step * REVEAL_STAGGER_STEP_MS}ms`,
		);
	});
}

// Replays the fade/slide-in for a container's reveal targets (e.g. a tab
// panel that was just switched to), even if they already played once.
function replayReveal(container: Element): void {
	const elements = Array.from(
		container.querySelectorAll<HTMLElement>('.reveal'),
	);

	if (!elements.length) {
		return;
	}

	elements.forEach(element => element.classList.remove('in-view'));

	// Force layout so the browser paints the hidden state before we flip
	// back to in-view, otherwise the transition has nothing to animate from.
	void container.getBoundingClientRect();

	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			elements.forEach(element => element.classList.add('in-view'));
		});
	});
}

// Gently fades and slides cards and section labels into view as the visitor
// scrolls to them. Gated behind html.reveal-ready so that content stays
// visible if this never runs (JS disabled, IntersectionObserver missing).
// The hero's own elements animate on page load via CSS instead, so they are
// excluded here to avoid two systems fighting over the same properties.
function setupScrollReveal(): void {
	const targets = Array.from(
		document.querySelectorAll<HTMLElement>(
			[
				'.sheet',
				'.category',
				'.top-title',
				'.header-title h2',
				'.evidence-head h2',
				'.section-head h2',
				'.top-title + h2',
				'.section-eyebrow + h2',
				'.industry-card',
				'.job-card',
				'.offer-card',
				'.plan',
				'.related-card',
				'.example-card',
				'.docs-card',
				'.evidence-card',
				'.highlight-card',
				'.platform-diagram-item',
				'.product-gallery-item',
			].join(', '),
		),
	).filter(target => !target.closest('.main-hero'));

	if (!targets.length) {
		return;
	}

	const reducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)',
	).matches;

	if (reducedMotion || !('IntersectionObserver' in window)) {
		return;
	}

	assignRevealStagger(targets);

	// Apply the hidden starting state right away so it is what the browser
	// paints first, regardless of when observing starts.
	targets.forEach(target => target.classList.add('reveal'));
	document.documentElement.classList.add('reveal-ready');

	const createObserver = (threshold: number) =>
		new IntersectionObserver(
			(entries, obs) => {
				entries.forEach(entry => {
					if (!entry.isIntersecting) {
						return;
					}

					obs.unobserve(entry.target);

					// Cards already in view on load can have their very first
					// IntersectionObserver callback fire before that hidden
					// state above has actually been painted, which would skip
					// the transition entirely. Waiting two frames guarantees
					// at least one paint of the hidden state happens first, so
					// the fade/slide-in always plays, on-load cards included.
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							entry.target.classList.add('in-view');
						});
					});
				});
			},
			{ threshold, rootMargin: '0px 0px -40px 0px' },
		);

	// A target so tall that 15% of it never fits in the viewport would never
	// reach the threshold and stay invisible (the terms and conditions are a
	// single sheet of several screens), so those reveal on the first visible
	// pixel instead.
	const revealThreshold = 0.15;
	const isTooTall = (target: HTMLElement) =>
		target.offsetHeight * revealThreshold > window.innerHeight - 40;

	const observer = createObserver(revealThreshold);
	const tallObserver = createObserver(0);

	targets.forEach(target =>
		(isTooTall(target) ? tallObserver : observer).observe(target),
	);
}

function setupExpandButtons(): void {
	document.querySelectorAll('[data-expand]').forEach(element => {
		element.addEventListener('click', event => {
			event.preventDefault();

			const trigger = event.currentTarget as HTMLElement | null;
			if (!trigger) {
				return;
			}

			const expandId = trigger.dataset['expand'];
			if (!expandId) {
				return;
			}

			const expandItem = document.querySelector(`#${expandId}`);
			if (!expandItem) {
				return;
			}

			const isOpen = expandItem.classList.toggle('open');
			trigger.setAttribute('aria-expanded', String(isOpen));
		});
	});
}

// Category filters on the news and changelog overviews. A [data-filter-group]
// holds the buttons ([data-filter]) and the items ([data-category]); a
// [data-filter-section] inside it, such as a changelog month, hides when
// none of its items are left. The buttons ship hidden, so without this the
// page is the full list.
function setupFilters(): void {
	document
		.querySelectorAll<HTMLElement>('[data-filter-group]')
		.forEach(group => {
			const bar = group.querySelector<HTMLElement>('.filter-bar');
			const buttons =
				group.querySelectorAll<HTMLButtonElement>('[data-filter]');
			if (!bar || buttons.length < 3) {
				return;
			}
			bar.hidden = false;

			buttons.forEach(button => {
				button.addEventListener('click', () => {
					const filter = button.dataset['filter'];
					buttons.forEach(other =>
						other.setAttribute(
							'aria-pressed',
							String(other === button),
						),
					);
					group
						.querySelectorAll<HTMLElement>('[data-category]')
						.forEach(item => {
							item.hidden =
								filter !== 'all' &&
								item.dataset['category'] !== filter;
						});
					group
						.querySelectorAll<HTMLElement>('[data-filter-section]')
						.forEach(section => {
							section.hidden = !section.querySelector(
								'[data-category]:not([hidden])',
							);
						});
				});
			});
		});
}

// "Copy link" on a changelog row. Shown only where the clipboard API exists.
function setupCopyLinks(): void {
	if (!navigator.clipboard) {
		return;
	}
	document
		.querySelectorAll<HTMLButtonElement>('[data-copy-link]')
		.forEach(button => {
			const label = button.textContent ?? '';
			button.hidden = false;
			button.addEventListener('click', () => {
				const url = button.dataset['copyLink'];
				if (!url) {
					return;
				}
				navigator.clipboard
					.writeText(url)
					.then(() => {
						button.textContent = button.dataset['copied'] ?? label;
						window.setTimeout(
							() => (button.textContent = label),
							2000,
						);
					})
					.catch(() => window.location.assign(url));
			});
		});
}

function changeIframeSrc(): void {
	const iframe = document.getElementById(
		'example',
	) as HTMLIFrameElement | null;

	if (!iframe) {
		return;
	}

	const links = document.querySelectorAll('a[data-src]');
	for (const link of Array.from(links)) {
		link.addEventListener('click', event => {
			event.preventDefault(); // Prevent default anchor behavior
			const target = event.currentTarget;
			if (!target || !(target instanceof HTMLAnchorElement)) {
				return;
			}

			const newSrc = target.getAttribute('data-src');
			if (newSrc) {
				iframe.src = newSrc;
			} else {
				console.error(
					'data-src attribute not found on clicked element',
				);
			}
		});
	}
}

// Firefox does not yet support draggable=false
// https://bugzilla.mozilla.org/show_bug.cgi?id=1376369
function disableDraggables(): void {
	const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
	if (!isFirefox) {
		return;
	}

	const undraggables = document.querySelectorAll('[draggable=false]');
	for (const element of Array.from(undraggables)) {
		element.addEventListener('dragstart', event => {
			event.preventDefault();
			return false;
		});
	}
}

function extractPrice(element: Element): number | null {
	if (!element) {
		return null;
	}

	const price = element.textContent;
	if (!price) {
		return null;
	}

	const numericPricePart = /([\d.,]+)/g.exec(price);
	if (!numericPricePart || !numericPricePart.length) {
		return null;
	}
	const priceFloat = Number.parseFloat(numericPricePart[0].replace(',', '.'));
	if (Number.isNaN(priceFloat)) {
		return null;
	}

	return priceFloat;
}

function convertPrices(): void {
	const priceElements = document.querySelectorAll('[data-price="true"]');
	const hasToLocaleString =
		typeof String.prototype.toLocaleString === 'function';
	const websiteLanguage = getLanguage();
	// Do not touch Dutch prices, for Dutch users that have en-US as their browser locale.
	const locale = websiteLanguage === 'nl' ? 'nl-NL' : undefined;

	for (const element of Array.from(priceElements)) {
		const price = extractPrice(element);

		if (typeof price === 'number' && hasToLocaleString) {
			element.textContent = price.toLocaleString(locale, {
				style: 'currency',
				currency: 'EUR',
			});
		}

		element.classList.add('pricing-converted');
	}
}

function toggleSubmenuOnHover(link: HTMLElement): void {
	const submenuId = link.getAttribute('data-submenu');
	if (!submenuId) {
		return;
	}

	const submenu = document.querySelector(`#${submenuId}`);
	if (!submenu) {
		return;
	}

	function positionSubmenu(): void {
		const rect = link.getBoundingClientRect();
		const submenuEl = submenu as HTMLElement;
		const margin = 16;
		// Keep the (now wider) submenu within the viewport instead of
		// overflowing past the right edge when a trigger sits far right.
		const maxLeft = window.innerWidth - submenuEl.offsetWidth - margin;
		const left = Math.max(margin, Math.min(rect.left, maxLeft));
		submenuEl.style.left = `${left}px`;
	}

	let isOpen = false;

	const isMobile = (): boolean =>
		window.matchMedia('(max-width: 1200px)').matches;

	// Mobile: a "back" button closes the drill-down panel.
	submenu.querySelector('.sub-menu-back')?.addEventListener('click', () => {
		submenu.classList.remove('open');
	});

	// Hover only drives the submenu on desktop; mobile uses click (below).
	link.addEventListener('mouseenter', () => {
		if (isMobile()) return;
		if (!isOpen) {
			positionSubmenu();
			submenu.classList.add('open');
		}
	});

	link.addEventListener('mouseleave', () => {
		if (isMobile()) return;
		if (!submenu.matches(':hover')) {
			if (!isOpen) {
				submenu.classList.remove('open');
			}
			if (!submenu.matches(':hover')) {
				isOpen = false;
			}
		}
	});

	submenu.addEventListener('mouseenter', () => {
		if (isMobile()) return;
		if (!isOpen) {
			submenu.classList.add('open');
		}
	});

	submenu.addEventListener('mouseleave', () => {
		if (isMobile()) return;
		if (!isOpen) {
			submenu.classList.remove('open');
		}
		if (!link.matches(':hover')) {
			isOpen = false;
		}
	});

	link.addEventListener('click', event => {
		// Mobile: open the submenu as a drill-down panel instead of navigating.
		if (isMobile()) {
			event.preventDefault();
			submenu.classList.add('open');
			return;
		}

		const anchor = link.querySelector('a');
		if (!anchor) return;

		const currentPath = window.location.pathname;
		const linkPath = new URL(anchor.href).pathname;

		if (currentPath === linkPath) {
			event.preventDefault();
			isOpen = !isOpen;
			positionSubmenu();
			submenu.classList.toggle('open');
		}
	});
}

// A video facade (partials/vimeo.html with facade) ships its player inside a
// <template>, so nothing from Vimeo loads until the visitor presses play.
// The player goes in over the poster, which stays behind it to hold the size.
function setupVideoFacades(): void {
	document.querySelectorAll('.vimeo-facade-play').forEach(button => {
		button.addEventListener('click', () => {
			const facade = button.closest('.vimeo-facade');
			const template = facade?.querySelector('template');
			if (!facade || !template) {
				return;
			}

			const player = template.content.firstElementChild?.cloneNode(true);
			if (!(player instanceof HTMLIFrameElement)) {
				return;
			}

			facade.append(player);
			button.remove();
			player.focus();
		});
	});
}

function setupSubmenuLinks(): void {
	document.querySelectorAll('div[data-submenu]').forEach(link => {
		if (link instanceof HTMLElement) {
			toggleSubmenuOnHover(link);
		}
	});
}

function init(): void {
	setupLightbox();
	setupMenu();
	setupLanguageMenu();
	setupOfferToggle();
	setupFeatureConfiguratorTabs();
	setupScrollReveal();
	setupExpandButtons();
	setupFilters();
	setupCopyLinks();
	setupSubmenuLinks();
	setupVideoFacades();
	disableDraggables();
	changeIframeSrc();
	convertPrices();
}

if (document.readyState !== 'loading') {
	init();
} else {
	document.addEventListener('DOMContentLoaded', () => init());
}
